import { logger } from '@Pages/pageRuntime/lib/logger';
import { UnknownResourceError } from '@Pages/pageRuntime/sdk/UnknownResourceError';

import { AiComponentCompileError } from './AiComponentCompileError';
import { resolveOriginalLocation } from './pageSourceDiagnostics';

export type PageSdkErrorKind = 'compile' | 'runtime';
export type PageSdkErrorTelemetryMode = 'local' | 'remote';
export type PageSdkRenderSurface = 'editor-preview' | 'draft-preview' | 'published';

export function pageSdkErrorKind(error: Error): PageSdkErrorKind {
  return error instanceof AiComponentCompileError ? 'compile' : 'runtime';
}

function isUnknownResourceError(error: Error): boolean {
  let current: unknown = error;

  while (current instanceof Error) {
    if (current instanceof UnknownResourceError) {
      return true;
    }

    current = current.cause;
  }

  return false;
}

/**
 * Editor previews render unsaved author code, so their failures are authoring
 * feedback rather than product errors and stay out of remote telemetry.
 * Unknown-resource lookups are authoring/policy conditions (the page
 * references a resource the viewer's policies don't expose), not platform
 * faults, so they stay out of remote telemetry too.
 */
export function pageSdkErrorTelemetryMode(
  error: Error,
  renderSurface: PageSdkRenderSurface
): PageSdkErrorTelemetryMode {
  if (isUnknownResourceError(error)) {
    return 'local';
  }

  return renderSurface === 'editor-preview' ? 'local' : 'remote';
}

export function reportPageSdkError(
  error: Error,
  {
    kind,
    source,
    componentStack,
    telemetryMode,
    renderSurface,
    sourcePath
  }: {
    kind: PageSdkErrorKind;
    source: 'page' | 'section';
    componentStack?: string | null;
    telemetryMode: PageSdkErrorTelemetryMode;
    renderSurface: PageSdkRenderSurface;
    sourcePath?: string;
  }
): void {
  const message = `[Page SDK] ${kind === 'compile' ? 'Compile' : 'Runtime'} error`;
  const originalLocation = resolveOriginalLocation(error);
  const compileSourcePath = error instanceof AiComponentCompileError ? error.sourcePath : undefined;
  const context = {
    kind,
    source,
    componentStack: componentStack ?? undefined,
    renderSurface,
    sourcePath: originalLocation?.sourcePath ?? compileSourcePath ?? sourcePath,
    ...(originalLocation
      ? {
          codeHash: originalLocation.codeHash,
          originalLine: originalLocation.line,
          originalColumn: originalLocation.column
        }
      : {})
  };

  if (telemetryMode === 'local') {
    logger.localError(message, { error, context });
    return;
  }

  logger.error(message, { error, context, consoleFallback: true, skipConsole: true });
}
